import { useQuery } from "@tanstack/react-query"

import { api } from "@/api/cliente"

/**
 * El bloque de expediente dentro del alta de un usuario.
 *
 * ⚠ **Existe para que dar de alta a alguien sea UN acto.** Antes eran tres —crear la
 * cuenta, crear la ficha, enlazarlas— y entre el primero y el tercero el sistema
 * queda con una cuenta sin expediente y un expediente sin cuenta, que es justo lo
 * que nadie se acuerda de terminar. El servidor las hace en una sola transacción: si
 * la ficha falla por una cédula repetida, la cuenta tampoco se crea.
 *
 * ⚠ **«Sin ficha» es el valor por defecto, y es lo correcto.** La mayoría de las
 * cuentas del portal no llevan expediente, y una cuenta de servicio tampoco. Con
 * «crear ficha» de fábrica, cada alta de un importador abriría un expediente de
 * personal que nadie pidió.
 *
 * ⚠ **La tercera opción no es un adorno.** Quien entró de chofer sin cuenta ya tiene
 * expediente; sin «enlazar a una existente», darle acceso le duplicaría la ficha y
 * a partir de ahí hay dos verdades sobre la misma persona.
 *
 * ⚠ **Los apellidos se piden aparte.** `usuarios.nombre` es el nombre completo en un
 * campo; la ficha los guarda separados porque sin eso no se ordena por apellido.
 * Se proponen partiendo el nombre por el primer espacio —que acierta en la mitad de
 * los casos y se corrige en la otra mitad— en vez de dejarlo en blanco.
 */

export type Modo = "" | "NUEVA" | "EXISTENTE"

export type DatosFicha = {
  modo: Modo
  persona_id: string
  tipo: "EMPLEADO" | "DIRECTIVA" | "CLIENTE"
  nombres: string
  apellidos: string
  tipo_documento: string
  documento: string
  cargo_id: string
}

export const FICHA_VACIA: DatosFicha = {
  modo: "",
  persona_id: "",
  tipo: "EMPLEADO",
  nombres: "",
  apellidos: "",
  tipo_documento: "V",
  documento: "",
  cargo_id: "",
}

/** Lo que el servidor espera, o `undefined` si no se pidió ficha. */
export function cuerpoDeLaFicha(f: DatosFicha): Record<string, unknown> | undefined {
  if (f.modo === "") return undefined
  if (f.modo === "EXISTENTE") return { modo: "EXISTENTE", persona_id: Number(f.persona_id) }
  return {
    modo: "NUEVA",
    tipo: f.tipo,
    nombres: f.nombres,
    apellidos: f.apellidos,
    tipo_documento: f.tipo_documento,
    documento: f.documento,
    // `null` y no cadena vacía, y un NÚMERO: un `<select>` siempre da texto.
    cargo_id: f.cargo_id === "" ? null : Number(f.cargo_id),
  }
}

/** Un nombre completo, partido por el primer espacio. Ver la cabecera. */
export function partirNombre(completo: string): { nombres: string; apellidos: string } {
  const limpio = completo.trim()
  const corte = limpio.indexOf(" ")
  if (corte === -1) return { nombres: limpio, apellidos: "" }
  return { nombres: limpio.slice(0, corte), apellidos: limpio.slice(corte + 1) }
}

type PersonaLibre = {
  id: number
  nombres: string
  apellidos: string
  tipo_documento: string
  documento: string
}

export function FichaDelAlta({
  ficha,
  onCambiar,
  nombreDelUsuario,
}: {
  ficha: DatosFicha
  onCambiar: (f: DatosFicha) => void
  nombreDelUsuario: string
}) {
  const libres = useQuery({
    queryKey: ["personas-sin-cuenta"],
    queryFn: () => api.get<PersonaLibre[]>("/api/v1/personas/sin-cuenta"),
    enabled: ficha.modo === "EXISTENTE",
  })

  const cargos = useQuery({
    queryKey: ["cargos", "activos"],
    queryFn: () => api.get<{ id: number; nombre: string }[]>("/api/v1/cargos?solo_activos=true"),
    enabled: ficha.modo === "NUEVA" && ficha.tipo !== "CLIENTE",
  })

  const poner = (parcial: Partial<DatosFicha>) => onCambiar({ ...ficha, ...parcial })

  return (
    <fieldset className="mt-5 border-t pt-4" style={{ borderColor: "var(--border)" }}>
      <legend className="sr-only">Expediente de la persona</legend>
      <label htmlFor="u-ficha-modo" className="mb-1 block text-xs font-medium uppercase">
        Expediente de la persona
      </label>
      <select
        id="u-ficha-modo"
        className="campo"
        data-testid="modo-ficha"
        value={ficha.modo}
        onChange={(e) => {
          const modo = e.target.value as Modo
          // Al elegir «nueva» se proponen los nombres del propio usuario: escribirlos
          // dos veces en el mismo formulario es la clase de trabajo que nadie hace.
          if (modo === "NUEVA" && ficha.nombres === "" && ficha.apellidos === "") {
            poner({ modo, ...partirNombre(nombreDelUsuario) })
          } else {
            poner({ modo })
          }
        }}
      >
        <option value="">Sin expediente — sólo la cuenta</option>
        <option value="NUEVA">Crear su expediente ahora</option>
        <option value="EXISTENTE">Enlazar a un expediente que ya existe</option>
      </select>

      {ficha.modo === "NUEVA" && (
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="u-ficha-tipo" className="mb-1 block text-xs font-medium uppercase">
              Tipo de expediente
            </label>
            <select
              id="u-ficha-tipo"
              className="campo"
              value={ficha.tipo}
              onChange={(e) => poner({ tipo: e.target.value as DatosFicha["tipo"] })}
            >
              <option value="EMPLEADO">Empleado</option>
              <option value="DIRECTIVA">Directiva</option>
              <option value="CLIENTE">Contacto de un cliente</option>
            </select>
          </div>

          <div>
            <label htmlFor="u-ficha-nombres" className="mb-1 block text-xs font-medium uppercase">
              Nombres
            </label>
            <input
              id="u-ficha-nombres"
              required
              minLength={2}
              className="campo"
              value={ficha.nombres}
              onChange={(e) => poner({ nombres: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="u-ficha-apellidos" className="mb-1 block text-xs font-medium uppercase">
              Apellidos
            </label>
            <input
              id="u-ficha-apellidos"
              required
              minLength={2}
              className="campo"
              value={ficha.apellidos}
              onChange={(e) => poner({ apellidos: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="u-ficha-tipodoc" className="mb-1 block text-xs font-medium uppercase">
              Documento
            </label>
            <select
              id="u-ficha-tipodoc"
              className="campo"
              value={ficha.tipo_documento}
              onChange={(e) => poner({ tipo_documento: e.target.value })}
            >
              {["V", "E", "P", "J", "G"].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="u-ficha-doc" className="mb-1 block text-xs font-medium uppercase">
              Número
            </label>
            <input
              id="u-ficha-doc"
              required
              minLength={4}
              className="campo"
              value={ficha.documento}
              onChange={(e) => poner({ documento: e.target.value })}
            />
          </div>

          {ficha.tipo !== "CLIENTE" && (
            <div className="md:col-span-2">
              <label htmlFor="u-ficha-cargo" className="mb-1 block text-xs font-medium uppercase">
                Cargo
              </label>
              {/* Del catálogo. Sólo los activos: lo retirado no se le asigna a nadie
                  nuevo, aunque siga explicando qué hacía quien lo tuvo. */}
              <select
                id="u-ficha-cargo"
                className="campo"
                value={ficha.cargo_id}
                onChange={(e) => poner({ cargo_id: e.target.value })}
              >
                <option value="">Sin cargo</option>
                {(cargos.data ?? []).map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {ficha.modo === "EXISTENTE" && (
        <div className="mt-3">
          <label htmlFor="u-ficha-persona" className="mb-1 block text-xs font-medium uppercase">
            Expediente
          </label>
          <select
            id="u-ficha-persona"
            required
            className="campo"
            data-testid="persona-existente"
            value={ficha.persona_id}
            onChange={(e) => poner({ persona_id: e.target.value })}
          >
            <option value="">Elige uno…</option>
            {(libres.data ?? []).map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.apellidos}, {p.nombres} · {p.tipo_documento}-{p.documento}
              </option>
            ))}
          </select>
          {libres.data?.length === 0 && (
            <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
              No hay expedientes sin cuenta. Todos los que existen ya tienen una.
            </p>
          )}
        </div>
      )}
    </fieldset>
  )
}
